/**
 * Outil « Carré dont on règle l'aire » (Boîte à outils, 4e → 2nde).
 *
 * On règle l'aire A d'un carré : son côté vaut √A. Les carrés parfaits
 * voisins n² et (n + 1)² l'entourent (quadrillage d'unités dessiné dans le
 * plus petit), d'où n ≤ √A < n + 1, placé sur la droite graduée ; en 3e on
 * zoome au dixième puis au centième, carrés exacts à l'appui. En 2nde, on
 * découpe le carré en k × k carrés d'aire b : √A = k√b. Calculs : `roots.ts`.
 */

import { useState } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { Note, ParamSlider, Segmented, Stepper, ToggleChip } from '../../ui/controls';
import { FIGURE_BG_CLASS, HaloText } from '../../ui/svg-parts';
import { bracket, isPerfectSquare, isqrt, MAX_AREA, scaled, simplify, truncated } from './roots';

const W = 320;
const H = 222;
const X0 = 40;
const Y0 = 194;
const REAL = 'var(--color-primary-500)';
const INNER = 'var(--color-success-600)';
const OUTER = 'var(--color-ink-400)';

const tx = (s: string) => s.replace(/,/g, '{,}');
/** graduations : « 3,40 » → « 3,4 », « 3,0 » → « 3 » */
const trimZeros = (s: string) => (s.includes(',') ? s.replace(/0+$/, '').replace(/,$/, '') : s);

export function RootSquareTool({ onSend, sendCost, level }: ToolProps & { level?: string | null }) {
  const rank = schoolLevelRank(level);
  const decimalsOk = rank === null || rank >= 4;
  const simplifyOk = rank === null || rank >= 5;
  const [area, setArea] = useState(12);
  const [digits, setDigits] = useState(0);
  const [cut, setCut] = useState(false);

  const d = decimalsOk ? digits : 0;
  const n = isqrt(area);
  const perfect = isPerfectSquare(area);
  const side = Math.sqrt(area);
  const br = bracket(area, d);
  const { k, b } = simplify(area);
  const canCut = simplifyOk && k > 1 && b > 1;
  const showCut = canCut && cut;
  const root = perfect ? String(n) : `√${area}`;

  // échelle : le carré (n + 1)² occupe toujours à peu près la même place
  const U = Math.max(12, Math.min(40, 170 / (n + 1)));
  const sq = (s: number) => ({ x: X0, y: Y0 - s * U, w: s * U });
  const inner = sq(n);
  const outer = sq(n + 1);
  const real = sq(side);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Stepper label="Aire du carré" value={area} min={0} max={MAX_AREA} onChange={setArea} />
        <ParamSlider
          name="Aire"
          ariaLabel="Aire du carré (curseur)"
          value={area}
          min={0}
          max={MAX_AREA}
          step={1}
          onChange={setArea}
        />
      </div>

      {(decimalsOk || canCut) && (
        <div className="flex flex-wrap items-center gap-2">
          {decimalsOk && (
            <Segmented
              ariaLabel="Précision de l’encadrement"
              value={String(digits)}
              options={[
                { value: '0', label: 'À l’unité' },
                { value: '1', label: 'Au dixième' },
                { value: '2', label: 'Au centième' },
              ]}
              onChange={(v) => setDigits(Number(v))}
            />
          )}
          {canCut && (
            <ToggleChip on={cut} onClick={() => setCut((c) => !c)}>
              Découper : {k}√{b}
            </ToggleChip>
          )}
        </div>
      )}

      <div className="mx-auto w-full max-w-[26rem]">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className={cn(
            'border-ink-100 block w-full select-none rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="img"
          aria-label={
            perfect
              ? `Carré d’aire ${area}, de côté ${n}.`
              : `Carré d’aire ${area}, de côté √${area}, entre le carré d’aire ${n * n} (côté ${n}) et le carré d’aire ${(n + 1) * (n + 1)} (côté ${n + 1}).`
          }
          data-testid="rs-figure"
        >
          {!perfect && (
            <rect
              x={outer.x}
              y={outer.y}
              width={outer.w}
              height={outer.w}
              fill="none"
              stroke={OUTER}
              strokeWidth={1.5}
              strokeDasharray="5 4"
            />
          )}
          <rect
            x={real.x}
            y={real.y}
            width={real.w}
            height={real.w}
            fill={REAL}
            fillOpacity={0.16}
          />
          {/* quadrillage d'unités dans le carré parfait n × n */}
          {!showCut &&
            Array.from({ length: Math.max(0, n - 1) }, (_, i) => (
              <g key={i} stroke={INNER} strokeOpacity={0.35} strokeWidth={1}>
                <line x1={X0 + (i + 1) * U} x2={X0 + (i + 1) * U} y1={inner.y} y2={Y0} />
                <line x1={X0} x2={X0 + inner.w} y1={Y0 - (i + 1) * U} y2={Y0 - (i + 1) * U} />
              </g>
            ))}
          {!perfect && n > 0 && !showCut && (
            <rect
              x={inner.x}
              y={inner.y}
              width={inner.w}
              height={inner.w}
              fill="none"
              stroke={INNER}
              strokeWidth={2}
              strokeDasharray="5 3"
            />
          )}
          {showCut &&
            Array.from({ length: k - 1 }, (_, i) => (
              <g key={`c${i}`} stroke={REAL} strokeWidth={1.5}>
                <line
                  x1={X0 + ((i + 1) * real.w) / k}
                  x2={X0 + ((i + 1) * real.w) / k}
                  y1={real.y}
                  y2={Y0}
                />
                <line
                  x1={X0}
                  x2={X0 + real.w}
                  y1={Y0 - ((i + 1) * real.w) / k}
                  y2={Y0 - ((i + 1) * real.w) / k}
                />
              </g>
            ))}
          {area > 0 && (
            <rect
              x={real.x}
              y={real.y}
              width={real.w}
              height={real.w}
              fill="none"
              stroke={REAL}
              strokeWidth={2.5}
            />
          )}
          {showCut && (
            <HaloText
              x={X0 + real.w / k / 2}
              y={Y0 - real.w / k / 2 + 4}
              textAnchor="middle"
              fontSize={12}
              fontWeight={700}
              fill={REAL}
            >
              {b}
            </HaloText>
          )}
          {area > 0 && !showCut && (
            <HaloText
              x={X0 + real.w / 2}
              y={Y0 - real.w / 2 + 5}
              textAnchor="middle"
              fontSize={14}
              fontWeight={700}
              fill="var(--color-ink-900)"
            >
              {`aire ${area}`}
            </HaloText>
          )}
          <HaloText
            x={X0 + Math.max(real.w, 24) / 2}
            y={Y0 + 18}
            textAnchor="middle"
            fontSize={14}
            fontWeight={700}
            fill={REAL}
          >
            {`côté ${root}`}
          </HaloText>
        </svg>
        {!perfect && (
          <p className="mt-1 flex flex-wrap justify-center gap-x-4 text-xs font-semibold">
            {!showCut && (
              <span className="text-success-600">
                ┅ {n} × {n} = {n * n}
              </span>
            )}
            <span className="text-primary-600">▢ aire {area}</span>
            <span className="text-ink-500">
              ┅ {n + 1} × {n + 1} = {(n + 1) * (n + 1)}
            </span>
          </p>
        )}
      </div>

      <NumberLine area={area} n={n} digits={d} />

      {/* une seule phrase courte pour le lecteur d'écran, pas tout le bloc à chaque cran */}
      <p className="sr-only" aria-live="polite">
        {perfect
          ? `Aire ${area} : côté ${n}.`
          : `Aire ${area} : √${area} est entre ${n} et ${n + 1}${d > 0 && !br.exact ? `, entre ${br.lo} et ${br.hi}` : ''}.`}
      </p>
      <section
        className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3"
        data-testid="rs-result"
      >
        <div className="flex flex-wrap items-center gap-2">
          <MathText className="text-ink-900 min-w-0 flex-1 text-lg">
            {perfect
              ? `$\\sqrt{${area}} = ${n}$ car $${n}^2 = ${area}$`
              : `$${n}^2 = ${n * n} < ${area} < ${(n + 1) * (n + 1)} = ${n + 1}^2$`}
          </MathText>
          {onSend && (
            <SendToSheetButton
              label={root}
              cost={sendCost}
              onClick={() => onSend({ atoms: perfect ? [String(n)] : ['√', String(area)] })}
            />
          )}
        </div>
        {!perfect && (
          <p className="text-ink-700 text-sm" data-testid="rs-unit">
            donc {n} &lt; √{area} &lt; {n + 1} : le côté est entre {n} et {n + 1}.
          </p>
        )}
        {!perfect && d > 0 && (
          <div data-testid="rs-decimal">
            <MathText className="text-ink-900 block overflow-x-auto">
              {br.exact
                ? `$${tx(br.lo)}^2 = ${tx(br.loSq)}$, donc $\\sqrt{${area}} = ${tx(br.lo)}$`
                : `$${tx(br.lo)}^2 = ${tx(br.loSq)} < ${area} < ${tx(br.hiSq)} = ${tx(br.hi)}^2$, donc $${tx(br.lo)} < \\sqrt{${area}} < ${tx(br.hi)}$`}
            </MathText>
          </div>
        )}
        {!perfect && decimalsOk && (
          <p className="text-ink-600 text-sm">
            √{area} = {truncated(area)}… : son écriture décimale ne s’arrête jamais, √{area} n’est
            pas un nombre décimal.
          </p>
        )}
        {canCut && (
          <div className="flex flex-wrap items-center gap-2" data-testid="rs-simplify">
            <MathText className="text-ink-900 min-w-0 flex-1 overflow-x-auto">
              {`$\\sqrt{${area}} = \\sqrt{${k * k} \\times ${b}} = \\sqrt{${k * k}} \\times \\sqrt{${b}} = ${k}\\sqrt{${b}}$`}
            </MathText>
            {onSend && (
              <SendToSheetButton
                label={`${k}√${b}`}
                cost={sendCost}
                onClick={() => onSend({ atoms: [String(k), '√', String(b)] })}
              />
            )}
          </div>
        )}
      </section>

      <Note live={false}>
        {showCut
          ? `Le carré se découpe en ${k} × ${k} = ${k * k} petits carrés d’aire ${b} : chacun a pour côté √${b}, et le grand côté vaut ${k} fois √${b}.`
          : `Le côté d’un carré d’aire ${area} est le nombre positif dont le carré vaut ${area} : c’est ${root}.`}
      </Note>
    </div>
  );
}

/** Droite graduée de 0 à 13, et un zoom au dixième ou au centième. */
function NumberLine({ area, n, digits }: { area: number; n: number; digits: number }) {
  const L = 16;
  const R = W - 16;
  const MAXV = 13;
  const xOf = (v: number) => L + (v / MAXV) * (R - L);
  const s = Math.sqrt(area);
  const perfect = isPerfectSquare(area);
  // zoom : de a à a + pas×10, graduations au pas
  const zoom = digits > 0 && !perfect ? bracket(area, digits - 1) : null;
  const step = 10 ** -digits;
  const a = zoom ? zoom.k * 10 ** -(digits - 1) : 0;
  const zx = (v: number) => L + ((v - a) / (10 * step)) * (R - L);
  const br = digits > 0 ? bracket(area, digits) : null;
  return (
    <div className="mx-auto w-full max-w-[26rem] space-y-1">
      <svg
        viewBox={`0 0 ${W} ${zoom ? 112 : 56}`}
        className={cn('block w-full select-none', FIGURE_BG_CLASS)}
        role="img"
        aria-label={
          perfect
            ? `Sur la droite graduée, √${area} tombe sur ${n}.`
            : `Sur la droite graduée, √${area} est entre ${n} et ${n + 1}${br && !br.exact ? `, et entre ${br.lo} et ${br.hi}` : ''}.`
        }
        data-testid="rs-line"
      >
        <line x1={L} x2={R} y1={22} y2={22} stroke="var(--color-ink-400)" strokeWidth={1.5} />
        {!perfect && (
          <line
            x1={xOf(n)}
            x2={xOf(n + 1)}
            y1={22}
            y2={22}
            stroke={INNER}
            strokeWidth={6}
            strokeLinecap="round"
          />
        )}
        {Array.from({ length: MAXV + 1 }, (_, v) => (
          <g key={v}>
            <line x1={xOf(v)} x2={xOf(v)} y1={17} y2={27} stroke="var(--color-ink-500)" />
            <text x={xOf(v)} y={44} textAnchor="middle" fontSize={12} fill="var(--color-ink-600)">
              {v}
            </text>
          </g>
        ))}
        <circle cx={xOf(s)} cy={22} r={5} fill={REAL} />
        {zoom && (
          <g>
            <line
              x1={xOf(a)}
              x2={L}
              y1={27}
              y2={74}
              stroke="var(--color-ink-300)"
              strokeDasharray="3 3"
            />
            <line
              x1={xOf(a + 10 * step)}
              x2={R}
              y1={27}
              y2={74}
              stroke="var(--color-ink-300)"
              strokeDasharray="3 3"
            />
            <line x1={L} x2={R} y1={78} y2={78} stroke="var(--color-ink-400)" strokeWidth={1.5} />
            {br && !br.exact && (
              <line
                x1={zx(br.k * step)}
                x2={zx((br.k + 1) * step)}
                y1={78}
                y2={78}
                stroke={INNER}
                strokeWidth={6}
                strokeLinecap="round"
              />
            )}
            {Array.from({ length: 11 }, (_, i) => (
              <g key={i}>
                <line
                  x1={zx(a + i * step)}
                  x2={zx(a + i * step)}
                  y1={73}
                  y2={83}
                  stroke="var(--color-ink-500)"
                />
                {(digits === 1 || i % 2 === 0) && (
                  <text
                    x={zx(a + i * step)}
                    y={100}
                    textAnchor="middle"
                    fontSize={12}
                    fill="var(--color-ink-600)"
                  >
                    {trimZeros(scaled(Math.round((a + i * step) * 10 ** digits), digits))}
                  </text>
                )}
              </g>
            ))}
            <circle cx={zx(s)} cy={78} r={5} fill={REAL} />
          </g>
        )}
      </svg>
    </div>
  );
}
